<script lang="ts">
  import { showAlert } from '../lib/ui-alert';
  import FieldTypeIcon from './FieldTypeIcon.svelte';
  import Icon from './Icon.svelte';
  /**
   * Drag-and-drop "board" для управління custom-полями: групи як collapsible-картки,
   * усередині кожної — рядки полів. Підтримує:
   *   - reorder груп (drag за ⋮⋮-handle)
   *   - reorder полів у межах групи
   *   - перенесення поля між групами (drag з однієї картки в іншу)
   *   - collapse/expand карток (chevron)
   *   - inline rename групи (клік на назву → input)
   *   - delete групи (іконка кошика; поля переходять у "без групи")
   *   - "+ Додати поле" знизу кожної групи
   *
   * Built-in поля рендеряться окремою фіксованою картою зверху (без drag/edit).
   */
  import { dndzone, type DndEvent } from 'svelte-dnd-action';
  import { flip } from 'svelte/animate';
  import { fieldType } from '../lib/data-tables-catalog';
  import {
    updateDataField,
    deleteDataField,
    reorderDataFields,
    createDataFieldGroup,
    updateDataFieldGroup,
    deleteDataFieldGroup,
    reorderDataFieldGroups,
    type ApiTable,
    type ApiField,
    type ApiFieldGroup,
  } from '../lib/data-tables-api';

  let {
    table,
    onReload,
    onOpenField,
    onAddField,
  }: {
    table: ApiTable;
    onReload: () => void | Promise<void>;
    /** Клік на полі → відкриває form-редактор (родитель керує view). */
    onOpenField: (slug: string, f: ApiField) => void;
    /** "+ Додати поле" → відкриває pick-type (родитель). */
    onAddField: (slug: string, groupName: string | null) => void;
  } = $props();

  const slug = $derived(table.slug);
  /** ВСІ поля (built-in + custom) у єдиному списку. Built-in отримують
   *  override-флаги (label/group/position/hidden) на бекенді через
   *  mergeBuiltInWithOverrides. */
  const allFields = $derived(table.fields);

  // === Локальна модель: групи + "_unnamed" як спец-група для NULL-полів ====
  const UNNAMED_ID = '__unnamed__';

  interface BoardGroup {
    id: string;
    name: string | null;
    fields: ApiField[];
  }

  /** Будуємо board: усі поля (built-in + custom) групуються однаково.
   *  Поля без groupName → у віртуальну "Без групи" внизу. */
  const board = $derived.by<BoardGroup[]>(() => {
    const namedGroups: BoardGroup[] = (table.groups ?? [])
      .slice()
      .sort((a, b) => a.position - b.position)
      .map((g) => ({
        id: g.id,
        name: g.name,
        fields: allFields
          .filter((f) => (f.groupName ?? null) === g.name)
          .sort((a, b) => (a.position ?? 0) - (b.position ?? 0)),
      }));
    const unnamedFields = allFields.filter((f) => !f.groupName);
    namedGroups.push({
      id: UNNAMED_ID,
      name: null,
      fields: unnamedFields.sort((a, b) => (a.position ?? 0) - (b.position ?? 0)),
    });
    return namedGroups;
  });

  /** UI-стан карток: яка згорнута (id → true). За замовч. усі розгорнуті. */
  let collapsed = $state<Record<string, boolean>>({});
  function toggle(id: string) {
    collapsed[id] = !collapsed[id];
  }

  /** Inline-перейменування групи: id → редаговане значення. */
  let editingGroupId = $state<string | null>(null);
  let editingGroupName = $state('');

  function startRename(g: BoardGroup) {
    if (!g.name) return; // віртуальну не можна
    editingGroupId = g.id;
    editingGroupName = g.name;
  }
  async function commitRename(g: BoardGroup) {
    if (!editingGroupName.trim() || editingGroupName.trim() === g.name) {
      editingGroupId = null;
      return;
    }
    busy = true;
    try {
      await updateDataFieldGroup(slug, g.id, { name: editingGroupName.trim() });
      editingGroupId = null;
      await onReload();
    } catch (e) {
      showAlert((e as Error).message);
    } finally {
      busy = false;
    }
  }
  function cancelRename() {
    editingGroupId = null;
  }

  let busy = $state(false);
  let newGroupOpen = $state(false);
  let newGroupName = $state('');

  async function addGroup() {
    const n = newGroupName.trim();
    if (!n) return;
    busy = true;
    try {
      await createDataFieldGroup(slug, n);
      newGroupName = '';
      newGroupOpen = false;
      await onReload();
    } catch (e) {
      showAlert((e as Error).message);
    } finally {
      busy = false;
    }
  }

  async function removeGroup(g: BoardGroup) {
    if (!g.name) return;
    if (!confirm(`Видалити групу "${g.name}"? Поля з неї перейдуть у "Додаткові".`)) return;
    busy = true;
    try {
      await deleteDataFieldGroup(slug, g.id);
      await onReload();
    } catch (e) {
      showAlert((e as Error).message);
    } finally {
      busy = false;
    }
  }

  async function removeField(f: ApiField) {
    if (f.protected) return;
    const msg = f.builtIn
      ? `Сховати поле "${f.label}"? Воно зникне з форми Деталей; саму колонку у БД не видалимо.`
      : `Видалити поле "${f.label}"?`;
    if (!confirm(msg)) return;
    busy = true;
    try {
      // Backend для built-in id виду "builtin:<slug>:<key>" робить
      // hidden=true; для custom UUID — повне видалення.
      await deleteDataField(slug, f.id);
      await onReload();
    } catch (e) {
      showAlert((e as Error).message);
    } finally {
      busy = false;
    }
  }

  // ===========================================================================
  //  DnD handlers: groups (зовнішня зона)
  // ===========================================================================

  let dragBoard = $state<BoardGroup[]>([]);

  // Sync локальної копії з пропсами (для оптимістичних DnD-оновлень).
  $effect(() => {
    dragBoard = JSON.parse(JSON.stringify(board)) as BoardGroup[];
  });

  function onGroupsDndConsider(e: CustomEvent<DndEvent<BoardGroup>>) {
    dragBoard = e.detail.items;
  }
  async function onGroupsDndFinalize(e: CustomEvent<DndEvent<BoardGroup>>) {
    dragBoard = e.detail.items;
    // Передаємо новий порядок на бекенд. Віртуальну "_unnamed" пропускаємо.
    const reorder = dragBoard
      .filter((g) => g.id !== UNNAMED_ID)
      .map((g, i) => ({ id: g.id, position: i }));
    if (reorder.length === 0) return;
    busy = true;
    try {
      await reorderDataFieldGroups(slug, reorder);
      await onReload();
    } catch (er) {
      showAlert((er as Error).message);
      await onReload();
    } finally {
      busy = false;
    }
  }

  // ===========================================================================
  //  DnD handlers: fields (внутрішня зона у кожній групі)
  // ===========================================================================

  function onFieldsDndConsider(groupId: string, e: CustomEvent<DndEvent<ApiField>>) {
    const idx = dragBoard.findIndex((g) => g.id === groupId);
    if (idx >= 0) dragBoard[idx]!.fields = e.detail.items;
  }
  async function onFieldsDndFinalize(group: BoardGroup, e: CustomEvent<DndEvent<ApiField>>) {
    const idx = dragBoard.findIndex((g) => g.id === group.id);
    if (idx >= 0) dragBoard[idx]!.fields = e.detail.items;
    // Зібрати глобальний reorder: усі поля з усіх груп з новими position та groupName.
    const items: { id: string; position: number; groupName: string | null }[] = [];
    for (const g of dragBoard) {
      const groupName = g.name; // null для UNNAMED_ID
      g.fields.forEach((f, i) => {
        items.push({ id: f.id, position: i, groupName });
      });
    }
    busy = true;
    try {
      await reorderDataFields(slug, items);
      await onReload();
    } catch (er) {
      showAlert((er as Error).message);
      await onReload();
    } finally {
      busy = false;
    }
  }

  // Тригер promote-to-primary (заздалегідь з UI як bookmark-іконка).
  // MVP: лише читання флагу f.primary — додамо ендпоінт пізніше.
</script>

<div class="space-y-3">
  <!-- Group cards with DnD -->
  <div
    use:dndzone={{
      items: dragBoard,
      type: 'groups',
      dropTargetStyle: {},
      flipDurationMs: 200,
    }}
    onconsider={onGroupsDndConsider}
    onfinalize={onGroupsDndFinalize}
    class="space-y-3"
  >
    {#each dragBoard as g (g.id)}
      <div
        animate:flip={{ duration: 200 }}
        class="rounded-xl border border-[var(--color-border-3)] bg-white shadow-sm"
      >
        <!-- Group header -->
        <div class="group/header flex items-center gap-3 px-3 py-2.5 border-b border-[var(--color-border-3)]">
          <!-- Drag handle -->
          {#if g.id !== UNNAMED_ID}
            <span
              class="inline-flex cursor-grab active:cursor-grabbing text-[var(--color-text-muted-2)] select-none"
              title="Перетягнути групу"
            ><Icon name="grip" size={14} /></span>
          {:else}
            <span class="w-3"></span>
          {/if}

          <!-- Title (inline-editable) -->
          {#if editingGroupId === g.id}
            <input
              bind:value={editingGroupName}
              onblur={() => commitRename(g)}
              onkeydown={(e) => {
                if (e.key === 'Enter') commitRename(g);
                if (e.key === 'Escape') cancelRename();
              }}
              class="flex-1 rounded border border-[var(--color-pu)] px-2 py-0.5 text-sm font-semibold"
              autofocus
            />
          {:else}
            <button
              type="button"
              class="flex-1 text-left text-sm font-semibold {g.name ? 'cursor-pointer' : 'italic text-[var(--color-text-muted)]'}"
              onclick={() => g.name && startRename(g)}
              ondblclick={() => startRename(g)}
              title={g.name ? 'Клік щоб перейменувати' : ''}
            >
              {g.name ?? 'Без групи (Додаткові)'}
            </button>
          {/if}

          {#if g.name && editingGroupId !== g.id}
            <button
              type="button"
              class="opacity-0 group-hover/header:opacity-100 transition-opacity rounded px-1.5 py-0.5 text-[11px] text-[var(--color-pu-3)] hover:bg-purple-100"
              onclick={() => startRename(g)}
              title="Перейменувати групу"
              aria-label="Перейменувати групу"
            ><Icon name="edit" size={13} /></button>
          {/if}

          <span class="text-[10px] rounded-full bg-[var(--color-bg-3)] px-2 py-0.5 text-[var(--color-text-muted)]">
            {g.fields.length}
          </span>

          {#if g.name}
            <button
              type="button"
              class="inline-flex items-center opacity-0 group-hover/header:opacity-100 transition-opacity text-[var(--color-text-muted-2)] hover:text-rose-600"
              onclick={() => removeGroup(g)}
              disabled={busy}
              title="Видалити групу"
            ><Icon name="delete" size={14} /></button>
          {/if}

          <!-- Collapse chevron -->
          <button
            type="button"
            class="inline-flex items-center text-[var(--color-text-muted)] hover:text-[var(--color-pu-3)] transition-transform {collapsed[g.id] ? '' : 'rotate-180'}"
            onclick={() => toggle(g.id)}
            title={collapsed[g.id] ? 'Розгорнути' : 'Згорнути'}
          ><Icon name="chevronDown" size={15} /></button>
        </div>

        <!-- Fields zone (collapsible) -->
        {#if !collapsed[g.id]}
          <div
            use:dndzone={{
              items: g.fields,
              type: 'fields',
              dropTargetStyle: { outline: '2px dashed var(--color-pu)' },
              flipDurationMs: 200,
            }}
            onconsider={(e) => onFieldsDndConsider(g.id, e)}
            onfinalize={(e) => onFieldsDndFinalize(g, e)}
            class="p-2 space-y-1.5 min-h-[44px]"
          >
            {#each g.fields as f (f.id)}
              {@const ft = fieldType(f.type)}
              <div
                animate:flip={{ duration: 200 }}
                class="group flex items-center gap-2 rounded-lg border border-[var(--color-border-3)] bg-white px-2 py-2 hover:border-[var(--color-pu)]/40 hover:bg-[var(--color-bg-3)] {f.hidden ? 'opacity-50' : ''}"
              >
                <span
                  class="inline-flex cursor-grab active:cursor-grabbing text-[var(--color-text-muted-2)] select-none"
                  title="Перетягнути поле"
                ><Icon name="grip" size={14} /></span>

                <button
                  type="button"
                  onclick={() => onOpenField(slug, f)}
                  class="flex flex-1 items-center gap-2 text-left min-w-0"
                >
                  <span class="grid h-6 w-6 shrink-0 place-items-center text-[12px] font-bold text-[var(--color-text-muted)]">
                    <FieldTypeIcon type={ft.key} size={14} />
                  </span>
                  <span class="text-[13px] truncate">{f.label}</span>
                  {#if f.required}<span class="text-red-500 text-[11px]">*</span>{/if}
                  {#if f.protected}
                    <span class="inline-flex text-[var(--color-text-muted-2)]" title="Ядрове поле — не можна сховати"><Icon name="lock" size={11} /></span>
                  {/if}
                  {#if !f.builtIn}
                    <span class="rounded bg-amber-100 px-1 text-[9px] font-bold uppercase tracking-wider text-amber-800">custom</span>
                  {/if}
                  {#if f.hidden}
                    <span class="rounded bg-slate-100 px-1 text-[9px] font-bold uppercase tracking-wider text-slate-600">hidden</span>
                  {/if}
                </button>

                {#if !f.protected}
                  <button
                    type="button"
                    class="inline-flex items-center opacity-0 group-hover:opacity-100 text-[var(--color-text-muted-2)] hover:text-rose-600 transition-opacity"
                    onclick={() => removeField(f)}
                    disabled={busy}
                    title={f.builtIn ? 'Сховати поле' : 'Видалити поле'}
                  >{#if f.builtIn}<Icon name="eyeOff" size={14} />{:else}<Icon name="delete" size={14} />{/if}</button>
                {/if}
              </div>
            {/each}

            {#if g.fields.length === 0}
              <div class="text-[11px] italic text-[var(--color-text-muted)] px-2 py-1">
                Перетягни сюди поле, або натисни «+ Додати поле».
              </div>
            {/if}

            <button
              type="button"
              onclick={() => onAddField(slug, g.name)}
              class="flex w-full items-center justify-center gap-2 rounded-lg border border-dashed border-[var(--color-border-3)] bg-white px-3 py-2 text-[11px] font-bold uppercase tracking-wider text-[var(--color-pu-3)] hover:bg-[var(--color-pu-light)]"
            >+ Додати поле</button>
          </div>
        {/if}
      </div>
    {/each}
  </div>

  <!-- Add group -->
  {#if newGroupOpen}
    <div class="flex items-center gap-2 rounded-xl border border-[var(--color-pu)]/40 bg-white p-2">
      <input
        bind:value={newGroupName}
        placeholder="Назва нової групи"
        onkeydown={(e) => {
          if (e.key === 'Enter') addGroup();
          if (e.key === 'Escape') { newGroupOpen = false; newGroupName = ''; }
        }}
        class="flex-1 rounded border border-[var(--color-border-3)] px-2.5 py-1.5 text-sm"
        autofocus
      />
      <button
        type="button"
        class="rounded-md bg-[var(--color-pu)] px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider text-white disabled:opacity-50"
        disabled={busy || !newGroupName.trim()}
        onclick={addGroup}
      >Створити</button>
      <button
        type="button"
        class="rounded-md border border-[var(--color-border-3)] px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider text-[var(--color-text-muted)]"
        onclick={() => { newGroupOpen = false; newGroupName = ''; }}
      >Скасувати</button>
    </div>
  {:else}
    <button
      type="button"
      onclick={() => (newGroupOpen = true)}
      class="flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-[var(--color-border-3)] bg-white px-3 py-3 text-sm font-bold uppercase tracking-wider text-[var(--color-pu-3)] hover:bg-[var(--color-pu-light)]"
    >+ Додати групу</button>
  {/if}
</div>
